import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

class ErrorBoundaryClass extends Component<
  Props & { t: (key: string) => string },
  State
> {
  constructor(props: Props & { t: (key: string) => string }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: {
        componentStack: errorInfo.componentStack,
      },
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { t } = this.props

      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-[var(--color-bg-primary)]">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold mb-4 text-[var(--color-text-primary)]">
              {t('error.title')}
            </h1>
            <p className="mb-6 text-[var(--color-text-secondary)]">
              {t('error.description')}
            </p>
            <button
              onClick={this.handleReload}
              className="px-6 py-2 rounded-lg font-medium bg-[var(--color-accent-red)] text-white hover:opacity-90 transition-opacity"
            >
              {t('error.reload')}
            </button>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-8 p-4 text-left text-sm overflow-auto rounded bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)]">
                {this.state.error.stack}
              </pre>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Wrapper to use hooks in class component
export function ErrorBoundary({ children, fallback }: Props) {
  const { t } = useTranslation()
  return (
    <ErrorBoundaryClass t={t} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  )
}
